import { OfferImage as ImageForOffer } from '@components/image/OfferImage'
import Inscription from '@components/display/offer/Inscription'
import Icon from '@components/shared/Icon'
import useT from '@hooks/useTranslation'
import formatPrice from '@utils/price/format'
import formatPriceRange from '@utils/price/formatPriceRange'
import { netPriceToGross } from '@utils/offers/netPriceToGross'

import styles from './OfferListItemNew.module.css'

const LIST_VARIANT_SRC_SET = {
    '568w': 'x0grid',
    '300w': 'list',
}

// Widok listy: zdjęcie ma stałą szerokość (.list-item .image).
const LIST_VARIANT_SRC_SET_SIZES = `280px`

const GRID_VARIANT_SRC_SET = {
    '1024w': 'x1grid',
    '568w': 'x0grid',
    '300w': 'list',
}

// Siatka listingu (OfferListItemView.module.css): 1 kolumna, od 600px 2, od 1000px 3, od 1100px obok filtrów.
const GRID_VARIANT_SRC_SET_SIZES = `(min-width: 1366px) 312px, (min-width: 1100px) calc((100vw - 426px) / 3), (min-width: 1000px) calc((100vw - 86px) / 3), (min-width: 600px) calc((100vw - 64px) / 2), calc(100vw - 42px)`

// Slajdy SimpleScrollSlider (SimpleScrollSlider.module.css), bez ramki karty.
const SLIDER_SRC_SET_SIZES = `(min-width: 1461px) 412px, (min-width: 1051px) calc((100vw - 100px) * 0.41625 - 2px), (min-width: 641px) calc(75vw - 47px), calc(87.5vw - 37px)`
const SLIDER_SMALL_SRC_SET_SIZES = `(min-width: 1461px) 247px, (min-width: 1051px) calc((100vw - 100px) * 0.41625 - 2px), (min-width: 641px) calc(75vw - 47px), calc(87.5vw - 37px)`

// Renderuje się zawsze (pusty div gdy brak danych), żeby sekcja rezerwowała
// stałą wysokość w gridzie karty niezależnie od tego, czy dane są dostępne.
function Subtitle({ offer }) {
	const { data, fuel } = offer || {}
	const { displacement_capacity: capacity, engine_power: power } = data || {}
	const parts = [
		capacity && `${capacity} cm³`,
		power && `${power} KM`,
		fuel,
	].filter(Boolean)

	return <div className={styles.subtitle}>{parts.join(' · ')}</div>
}

function Firm({ isFirm, user }) {
	const t = {
		firm: useT('firmowa'),
		offer: useT('Oferta'),
		private: useT('prywatna'),
	}

	const firm =
		typeof user === 'object'
			? isFirm
				? `${t.offer} ${t.firm}`
				: `${t.offer} ${t.private}`
			: null

	return firm ? <div className={styles.firm}>{firm}</div> : <span />
}

function Location({ location }) {
	return location ? (
		<div className={styles.location}>
			<Icon style={{ fontSize: '15rem', margin: 0 }}>
				location_on
			</Icon>{' '}
			{location}
		</div>
	) : null
}

function OfferImage({ alt, forList, highlighted, imageSizes, inscription, isFresh, offer, reserved, priority }) {
    const highlightedLabel = useT('Wyróżnione')

    return (
        <div className={styles.image}>
            {(inscription || reserved) && (
                <Inscription text={inscription} reserved={reserved} />
            )}
            {!forList && highlighted && (
                <span className={styles['highlighted-badge']}>{highlightedLabel}</span>
            )}
            {!forList && (
                <div className={styles['image-badges']}>
                    <IsNew offer={offer} />
                    <FreshBadge isFresh={isFresh} />
                </div>
            )}
            <ImageForOffer
                alt={alt}
                offer={offer}
                preferredOrder={0}
                preferredVariant="list"
                priority={priority}
                variantSrcSet={
                    forList ? LIST_VARIANT_SRC_SET : GRID_VARIANT_SRC_SET
                }
                variantSrcSetSizes={
                    imageSizes || (forList ? LIST_VARIANT_SRC_SET_SIZES : GRID_VARIANT_SRC_SET_SIZES)
                }
            />
        </div>
    )
}

function PriceDetails({ forList, inject, isBuy, offer }) {
	let grossPrice, price, wasNet

	if (isBuy) {
		price = formatPriceRange(offer.price, ' ')
	} else {
		[wasNet, grossPrice] = netPriceToGross(offer)
		price = formatPrice(grossPrice, ' ')
	}

	const currency = offer.price?.currency || 'PLN'

	return (
		<>
			<div className={styles.details}>
				{forList && inject?.details}
				<span className={styles.price}>
					<span className={styles.price} style={{display:'inline-block',margin:0}}>{price}</span> {currency}
				</span>
				{!forList && inject?.details}
			</div>
			{ wasNet && <span className={styles.netto}>{formatPrice(offer.price.price, ' ')} {currency} NETTO</span> }
		</>
	)
}

function Title({ caption, isPart, pre }) {
	const _pre = useT(pre)
	return (
		<h2
			className={isPart ? styles['part-title'] : styles.title}
		>
			<span>
				{pre && <span className={styles.teal}>{_pre}</span>} {caption}
			</span>
		</h2>
	)
}

function Installment({ installment }) {
	const t = {
		installment: useT('Rata'),
		mth: useT('mc'),
	}

	return installment ? (
		<div className={styles.installment}>
			{t.installment} {installment} PLN/{t.mth}
		</div>
	) : null
}

function IsNew({ offer }) {
	const { _new: isNew } = offer || {}
	const _new = useT('Nowy')
	if (!isNew) return null
	return <span className={styles.badge}>{_new}</span>
}

// Świeżość liczona raz na render listy i przekazana jako gotowy boolean,
// żeby uniknąć rozjazdu Date.now() między kartami tej samej listy.
function FreshBadge({ isFresh }) {
	const _fresh = useT('Nowa oferta')
	if (!isFresh) return null
	return <span className={styles['badge-fresh']}>{_fresh}</span>
}

function extractOfferData(props) {
	const { forList, offer, priority, ...rest } = props || {}
	const { data, price, tracking, user } = offer || {}
	const { equipment, inscription, dealer: isDealer } = data || {}
	const { installment } = price || {}
	const { reserved } = tracking || {}
	const { address, dealer } = user || {}
	const { firm_nip: firmNip } = dealer || {}
	const { city: userCity } = address || {}
	const { city: offerCity, trim } = tracking || {}
	const { title: trimTitle } = trim || {}
	const location = noStringGibberish(offerCity || userCity)

	const isBuy = offer.type === 'buy_ad' || offer.isBuy
	const isPart = offer.type === 'part_ad'
	const isFirm = isPart
		? Boolean(dealer && isDealer)
		: Boolean(firmNip)
	const isNew = Boolean(offer._new)

	return ({
			...rest,

			priority,
			equipment,
			forList,
			inscription,
			installment,
			isBuy,
			isFirm,
			isNew,
			isPart,
			location,
			offer,
			reserved,
			trimTitle,
			user,
	})
}

function noStringGibberish(string) {
	if (typeof string !== 'string') return null
	const trimmed = string.trim()
	if (['-'].includes(trimmed)) return null
	return trimmed
}

export {
	Firm,
	FreshBadge,
	Installment,
	IsNew,
	Location,
	OfferImage,
	PriceDetails,
	Subtitle,
	Title,

	SLIDER_SMALL_SRC_SET_SIZES,
	SLIDER_SRC_SET_SIZES,
	extractOfferData,
}
